import React, { MutableRefObject } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Markup } from 'react-render-markup';
import { Button, Card } from 'antd';
import { PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import type { Activity } from '../../../api/activities/interface';
import { Locale } from '../../../context/AppProvider';
import styles from './ClaimActivitiesContainer.module.scss';

dayjs.extend(utc);
const MAX_HEIGHT = 290;
interface ActivityFilterProps {
  activity: Activity;
  activityRefs?: MutableRefObject<Record<string, HTMLDivElement | null>>;
  hideBorder?: boolean;
  hideTitle?: boolean;
  removeMoreButton?: boolean;
  setSelectedActivity?: (activity: Activity) => void;
}

export const formatTitle = (title: string) => title;
export const formatDate = (date: string) =>
  dayjs(date).utc().format(PRETTY_DATE_FORMAT);

const ActivityCardDeprecated: React.FC<ActivityFilterProps> = ({
  activity,
  activityRefs,
  hideBorder,
  hideTitle,
  removeMoreButton = false,
  setSelectedActivity,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isTooLarge, setIsTooLarge] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { messages } = React.useContext(Locale);

  React.useEffect(() => {
    if (removeMoreButton) {
      setIsTooLarge(false);
      return;
    }

    const el = contentRef.current;
    if (el) {
      const shouldShowMore = el.scrollHeight > MAX_HEIGHT;
      setIsTooLarge(!isExpanded && shouldShowMore);
    }
  }, [activity.description_html, isExpanded, removeMoreButton]);

  return (
    <Card
      title={hideTitle ? null : formatTitle(activity.activity_subject)}
      type="inner"
      extra={hideTitle ? null : formatDate(activity.start_date)}
      className={clsx(styles.activityCard, { [styles.bordered]: !hideBorder })}
      ref={(elem) => {
        if (!activityRefs) {
          return;
        }

        activityRefs.current[activity.activity_id] = elem;
      }}
      onClick={() => setSelectedActivity?.(activity)}
    >
      <div
        ref={contentRef}
        style={{
          maxHeight: isExpanded ? 'none' : `${MAX_HEIGHT}px`,
          overflow: isExpanded ? 'visible' : 'hidden',
          position: 'relative',
          transition: 'max-height 0.3s ease',
        }}
      >
        {!isExpanded && isTooLarge && (
          <div
            style={{
              background:
                'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
              height: '60px',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              pointerEvents: 'none',
            }}
          />
        )}
        <Markup markup={activity.description_html} />
      </div>

      {(isTooLarge || isExpanded) && (
        <div className={styles.expandButton}>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded
              ? messages['claim_overview.activities.activity_expand.collapse']
              : messages['claim_overview.activities.activity_expand.expand']}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default ActivityCardDeprecated;
